/**
 * Device Detection Service
 * 
 * Provides device type detection functionality to determine whether the user
 * is accessing the application from a mobile device (tablet/phone) or desktop device.
 * This is used to route authentication flows appropriately.
 */

export interface DeviceDetector {
  isMobileDevice(): boolean;
  getDeviceType(): 'mobile' | 'desktop';
  getUserAgent(): string;
  isTablet(): boolean;
  isPhone(): boolean;
  isFireTablet(): boolean;
  getDeviceInfo(): {
    deviceType: 'mobile' | 'desktop';
    isTablet: boolean;
    isPhone: boolean;
    isFireTablet: boolean;
    screenWidth: number;
    screenHeight: number;
    userAgent: string;
  };
}

export class DeviceDetectionService implements DeviceDetector {
  private readonly mobileBreakpoint = 1024; // pixels
  private readonly userAgent: string;

  constructor() {
    this.userAgent = navigator.userAgent;
  }

  /**
   * Determines if the current device is a mobile device (tablet or phone)
   * Uses both user agent detection and screen size as indicators
   */
  isMobileDevice(): boolean {
    // Check user agent for mobile indicators including Fire tablets
    const mobileUserAgentRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|KFAPWI|KFAPWA|KFARWI|KFASWI|KFJWI|KFJWA|KFSOWI|KFTHWI|KFTHWA|KFOT|KFTT|KFMEWI|KFFOWI|KFSAWI|KFSAWA|KFTRWI|KFTRWA|KFMAWI|KFKAWI|Silk/i;
    const isMobileUserAgent = mobileUserAgentRegex.test(this.userAgent);

    // Specific check for Fire tablets (they often don't include "Mobile" in user agent)
    const isFireTablet = this.isFireTablet();

    // Check screen size (tablets typically have smaller screens than desktops)
    const isSmallScreen = window.innerWidth < this.mobileBreakpoint;

    // Consider it mobile if user agent indicates mobile OR it's a Fire tablet OR screen is small
    return isMobileUserAgent || isFireTablet || isSmallScreen;
  }

  /**
   * Returns the device type classification
   */
  getDeviceType(): 'mobile' | 'desktop' {
    const isMobile = this.isMobileDevice();
    const deviceType = isMobile ? 'mobile' : 'desktop';
    
    console.log(`[DeviceDetection] User Agent: ${this.userAgent}`);
    console.log(`[DeviceDetection] Screen size: ${window.innerWidth}x${window.innerHeight}`);
    console.log(`[DeviceDetection] Is mobile: ${isMobile}, Device type: ${deviceType}`);
    
    return deviceType;
  }

  /**
   * Returns the current user agent string
   */
  getUserAgent(): string {
    return this.userAgent;
  }

  /**
   * Additional helper method to check if device is specifically a tablet
   * Tablets are considered mobile devices for authentication purposes
   */
  isTablet(): boolean {
    const tabletRegex = /iPad|Android(?!.*Mobile)|Tablet/i;
    const isGeneralTablet = tabletRegex.test(this.userAgent);
    
    // Also check for Fire tablets specifically
    const isFireTablet = this.isFireTablet();
    
    return isGeneralTablet || isFireTablet;
  }

  /**
   * Specific method to detect Amazon Fire tablets
   * Fire tablets have unique user agent patterns that need special handling
   */
  isFireTablet(): boolean {
    // Fire tablet model codes and Silk browser detection
    // Updated to include Fire Max 11 (KFTRWI, KFTRWA) and other newer models
    const fireTabletRegex = /KFAPWI|KFAPWA|KFARWI|KFASWI|KFJWI|KFJWA|KFSOWI|KFTHWI|KFTHWA|KFOT|KFTT|KFMEWI|KFFOWI|KFSAWI|KFSAWA|KFTRWI|KFTRWA|KFMAWI|KFKAWI|Silk/i;
    
    // Additional check for Fire OS and Amazon device indicators
    const fireOSRegex = /Fire|Amazon|AFTT|AFTB|AFTS|AFTM|AFTA|AFTKMST11|AFTRS|AFTEU014|AFTEU011|AFTR|AFTN|AFTS|AFTVA002/i;
    
    return fireTabletRegex.test(this.userAgent) || 
           (fireOSRegex.test(this.userAgent) && /Android/i.test(this.userAgent));
  }

  /**
   * Additional helper method to check if device is specifically a phone
   */
  isPhone(): boolean {
    const phoneRegex = /iPhone|Android.*Mobile|webOS|BlackBerry|IEMobile|Opera Mini/i;
    return phoneRegex.test(this.userAgent);
  }

  /**
   * Get detailed device information for debugging and logging
   */
  getDeviceInfo(): {
    deviceType: 'mobile' | 'desktop';
    isTablet: boolean;
    isPhone: boolean;
    isFireTablet: boolean;
    screenWidth: number;
    screenHeight: number;
    userAgent: string;
  } {
    return {
      deviceType: this.getDeviceType(),
      isTablet: this.isTablet(),
      isPhone: this.isPhone(),
      isFireTablet: this.isFireTablet(),
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
      userAgent: this.userAgent
    };
  }
}

// Export a singleton instance for use throughout the application
export const deviceDetectionService = new DeviceDetectionService();

export default deviceDetectionService;